import { useEffect, useMemo, useState } from 'react';
import { Alert, Pressable, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '@/theme';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { InputField } from '@/components/ui/InputField';
import { ThemedText, Caption, Body } from '@/components/ui/Text';
import { useUserStore } from '@/state/userStore';
import { updateItineraryItem, type ItineraryItem, type ItineraryItemNotes } from '@/services/itinerary';

type ItemNotesSectionProps = {
  item: ItineraryItem;
  canEdit?: boolean;
};

const normalizeText = (s: string) => s.replace(/\r\n/g, '\n');

export function ItemNotesSection({ item, canEdit = false }: ItemNotesSectionProps) {
  const theme = useTheme();
  const firebaseUser = useUserStore((s) => s.firebaseUser);
  const userId = firebaseUser?.uid || null;

  const initial = useMemo(() => {
    const t = (item.notes?.text || '').toString();
    return {
      text: normalizeText(t),
      isShared: Boolean(item.notes?.isShared),
    };
  }, [item.notes?.text, item.notes?.isShared]);

  const [text, setText] = useState(initial.text);
  const [isShared, setIsShared] = useState(initial.isShared);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setText(initial.text);
    setIsShared(initial.isShared);
  }, [initial.text, initial.isShared, item.id]);

  const hasNotes = text.trim().length > 0;
  const dirty = normalizeText(text) !== initial.text || isShared !== initial.isShared;

  const persist = async () => {
    if (!canEdit) return;
    if (!item.tripId) {
      Alert.alert('Unable to save', 'Missing trip context for this item.');
      return;
    }
    if (!userId) {
      Alert.alert('Sign in required', 'Please sign in to save notes.');
      return;
    }

    setSaving(true);
    try {
      const payload: ItineraryItemNotes | undefined = hasNotes
        ? {
            text: text.trim(),
            isShared,
            updatedAt: new Date(),
            updatedBy: userId,
          }
        : undefined;
      await updateItineraryItem(item.tripId, item.id, { notes: payload } as any);
    } catch (e: any) {
      Alert.alert('Error', e?.message || 'Failed to save notes. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const confirmClear = () => {
    if (!canEdit) return;
    if (!text.trim()) return;
    Alert.alert('Clear notes?', 'This will remove your notes from this item.', [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Clear',
        style: 'destructive',
        onPress: () => {
          setText('');
        },
      },
    ]);
  };

  return (
    <Card variant="outlined" style={{ padding: theme.spacing(1.25) }}>
      <View style={{ gap: theme.spacing(1) }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: theme.spacing(1) }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.75), flex: 1 }}>
            <Ionicons name="chatbubble-ellipses-outline" size={18} color={theme.colors.textSecondary} />
            <ThemedText variant="bodyStrong" style={{ fontSize: 14 }}>
              Notes
            </ThemedText>
          </View>

          {canEdit && (
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.75) }}>
              <Pressable
                onPress={() => setIsShared(false)}
                style={({ pressed }) => ({
                  paddingHorizontal: theme.spacing(1),
                  paddingVertical: theme.spacing(0.5),
                  borderRadius: theme.radii.pill,
                  backgroundColor: !isShared ? theme.colors.primary + '18' : theme.colors.borderMuted + '20',
                  opacity: pressed ? 0.7 : 1,
                })}
                accessibilityRole="button"
                accessibilityLabel="Private notes"
                accessibilityState={{ selected: !isShared }}
              >
                <Caption style={{ fontSize: 11, fontWeight: '700', color: !isShared ? theme.colors.primary : theme.colors.textSecondary }}>
                  Private
                </Caption>
              </Pressable>
              <Pressable
                onPress={() => setIsShared(true)}
                style={({ pressed }) => ({
                  paddingHorizontal: theme.spacing(1),
                  paddingVertical: theme.spacing(0.5),
                  borderRadius: theme.radii.pill,
                  backgroundColor: isShared ? theme.colors.primary + '18' : theme.colors.borderMuted + '20',
                  opacity: pressed ? 0.7 : 1,
                })}
                accessibilityRole="button"
                accessibilityLabel="Shared notes"
                accessibilityState={{ selected: isShared }}
              >
                <Caption style={{ fontSize: 11, fontWeight: '700', color: isShared ? theme.colors.primary : theme.colors.textSecondary }}>
                  Shared
                </Caption>
              </Pressable>
            </View>
          )}
        </View>

        {canEdit ? (
          <InputField
            placeholder='Add a note (e.g. "Kids loved this playground", "Come back for sunset")'
            value={text}
            onChangeText={setText}
            multiline
            numberOfLines={4}
            style={{ minHeight: 96 }}
          />
        ) : hasNotes ? (
          <Body style={{ lineHeight: 20 }}>{text.trim()}</Body>
        ) : (
          <Caption color={theme.colors.textSecondary}>No notes yet.</Caption>
        )}

        {canEdit && (
          <View style={{ flexDirection: 'row', gap: theme.spacing(1) }}>
            <Button
              label={saving ? 'Saving…' : 'Save note'}
              onPress={() => void persist()}
              disabled={!dirty || saving}
              variant="secondary"
              style={{ flex: 1 }}
            />
            <Button
              label="Clear"
              onPress={confirmClear}
              disabled={!text.trim().length || saving}
              variant="ghost"
              style={{ minWidth: 90 }}
            />
          </View>
        )}

        {canEdit && (
          <Caption color={theme.colors.textSecondary} style={{ fontSize: 11 }}>
            Private by default. Shared notes are visible to collaborators on this trip.
          </Caption>
        )}
      </View>
    </Card>
  );
}

